import { inject, Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  PaymentCallbackParams,
  PaymentCallbackResponse,
  PaymentResponse,
  PaymentStatusResponse,
  PayTransaction,
  SendEmailReceiptRequest,
  SendEmailReceiptResponse,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
  Transaction,
  UnifiedCallbackResponse,
  WalletPaymentResultResponse,
} from '@tabify/models/types';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TransactionService {
  private readonly http = inject(HttpClient);

  private readonly url = `${environment.api.baseUrl}${environment.api.v1.transactions}`;

  /**
   * Gets the open transaction and order items for a table by short code
   * @param shortCode The short code of the table
   */
  getTransactionByTableShortCode(shortCode: string): Observable<Transaction> {
    return this.http.get<Transaction>(`${this.url}/tables/${shortCode}`);
  }

  /**
   * Gets transaction details by transaction ID
   * @param transactionId The transaction ID
   */
  getTransactionById(transactionId: string): Observable<Transaction> {
    return this.http.get<Transaction>(`${this.url}/${transactionId}`);
  }

  payTransaction(payload: PayTransaction): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(`${this.url}`, payload);
  }

  /**
   * Processes payment callback from payment gateway.
   * Sends all query parameters (required + optional) to the backend.
   *
   * Returns the unified shape (`code`, `messageKey`, `gatewayMessage`, etc.).
   * The legacy shape is still accepted by `PaymentCallbackComponent` via a
   * tolerant adapter, so this typing is forward-looking.
   *
   * @param callbackParams All callback parameters from payment gateway (Worldline)
   */
  processPaymentCallback(
    callbackParams: PaymentCallbackParams,
  ): Observable<UnifiedCallbackResponse | PaymentCallbackResponse> {
    return this.http.get<UnifiedCallbackResponse | PaymentCallbackResponse>(
      `${this.url}/worldline/callback`,
      { params: { ...callbackParams } },
    );
  }

  /**
   * Sends email receipt to customer
   * @param request Receipt request containing email, payment ID, and transaction ID
   */
  sendReceipt(request: SendEmailReceiptRequest): Observable<SendEmailReceiptResponse> {
    return this.http.post<SendEmailReceiptResponse>(`${this.url}/send-receipt`, request);
  }

  /**
   * Process payment cancellation callback from Worldline
   * Called when user cancels payment in the wallet/payment UI
   * @param transactionId The transaction ID
   * @param paymentId The payment ID
   */
  processPaymentCancelled(transactionId: string, paymentId: string): Observable<WalletPaymentResultResponse> {
    return this.http.post<WalletPaymentResultResponse>(
      `${this.url}/${transactionId}/payments/${paymentId}/cancelled`,
      {}
    );
  }

  /**
   * Process payment declined callback from Worldline
   * Called when payment is declined by the payment provider
   * @param transactionId The transaction ID
   * @param paymentId The payment ID
   */
  processPaymentDeclined(transactionId: string, paymentId: string): Observable<WalletPaymentResultResponse> {
    return this.http.post<WalletPaymentResultResponse>(
      `${this.url}/${transactionId}/payments/${paymentId}/declined`,
      {}
    );
  }

  /**
   * Gets payment status to verify if a payment was processed despite SDK errors
   * Used as a fail-safe before showing payment error to the user
   */
  getPaymentStatus(transactionId: string, paymentId: string): Observable<PaymentStatusResponse> {
    return this.http.get<PaymentStatusResponse>(
      `${this.url}/${transactionId}/payments/${paymentId}/status`
    );
  }

  /** 404 means no feedback exists yet — the normal pre-rating case. */
  getFeedback(
    transactionId: string,
    paymentId: string,
  ): Observable<SubmitFeedbackResponse> {
    return this.http.get<SubmitFeedbackResponse>(
      `${this.url}/${transactionId}/payments/${paymentId}/feedback`,
    );
  }

  submitFeedback(
    transactionId: string,
    paymentId: string,
    request: SubmitFeedbackRequest,
  ): Observable<SubmitFeedbackResponse> {
    return this.http.put<SubmitFeedbackResponse>(
      `${this.url}/${transactionId}/payments/${paymentId}/feedback`,
      request,
    );
  }
}
